// src/auth/AuthProvider.tsx
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { googleLogout } from "@react-oauth/google";
import { jsx } from "react/jsx-runtime";
var AuthContext = createContext(void 0);
var DEFAULT_API_DOMAIN = "https://api.automations.easystreetbrands.com";
var sanitizeBaseUrl = (value) => {
  if (!value) {
    return "";
  }
  const trimmed = String(value).trim();
  if (!trimmed) {
    return "";
  }
  return trimmed.replace(/\/+$/, "");
};
var readProcessEnv = (key) => {
  if (typeof globalThis === "undefined") {
    return void 0;
  }
  return globalThis.process?.env?.[key];
};
var inferBrand = (importMeta) => {
  const envBrand = importMeta?.env?.VITE_BRAND || importMeta?.env?.VITE_BRAND_ID || readProcessEnv("VITE_BRAND") || readProcessEnv("VITE_BRAND_ID");
  if (typeof envBrand === "string" && envBrand.trim()) {
    return envBrand.trim().toLowerCase();
  }
  if (typeof window !== "undefined") {
    const hostname = window.location.hostname.toLowerCase();
    if (hostname.includes("hollow")) {
      return "hollow";
    }
    if (hostname.includes("marsmen")) {
      return "marsmen";
    }
  }
  return void 0;
};
var inferStage = (importMeta) => {
  const explicitStage = importMeta?.env?.VITE_API_STAGE || importMeta?.env?.VITE_STAGE || readProcessEnv("VITE_API_STAGE") || readProcessEnv("VITE_STAGE");
  if (explicitStage && explicitStage.trim()) {
    return explicitStage.trim();
  }
  const envName = importMeta?.env?.VITE_ENVIRONMENT || importMeta?.env?.NODE_ENV || readProcessEnv("VITE_ENVIRONMENT") || readProcessEnv("NODE_ENV");
  if (envName) {
    const normalized = envName.trim().toLowerCase();
    if (normalized.startsWith("prod")) {
      return "prod";
    }
    if (normalized.startsWith("stag")) {
      return "staging";
    }
    if (normalized.startsWith("dev")) {
      return "development";
    }
  }
  return "prod";
};
var buildUnifiedBaseUrl = (brand, stage) => {
  if (!brand) {
    return "";
  }
  const stageName = stage && stage.trim() ? stage.trim() : "prod";
  return `${DEFAULT_API_DOMAIN}/${brand}/${stageName}`;
};
var resolveApiBaseUrl = (explicit) => {
  const sanitizedExplicit = sanitizeBaseUrl(explicit);
  if (sanitizedExplicit) {
    return sanitizedExplicit;
  }
  const runtimeConfig = typeof window !== "undefined" && window.__APP_CONFIG__ || {};
  if (runtimeConfig.apiBaseUrl) {
    return sanitizeBaseUrl(runtimeConfig.apiBaseUrl);
  }
  const importMeta = typeof import.meta !== "undefined" ? import.meta : void 0;
  const envBase = sanitizeBaseUrl(importMeta?.env?.VITE_API_BASE_URL);
  if (envBase) {
    return envBase;
  }
  const envUrl = sanitizeBaseUrl(importMeta?.env?.VITE_API_URL || importMeta?.env?.API_ENDPOINT);
  if (envUrl) {
    return envUrl;
  }
  const nodeBase = sanitizeBaseUrl(readProcessEnv("VITE_API_BASE_URL"));
  if (nodeBase) {
    return nodeBase;
  }
  const nodeUrl = sanitizeBaseUrl(readProcessEnv("VITE_API_URL") || readProcessEnv("API_ENDPOINT"));
  if (nodeUrl) {
    return nodeUrl;
  }
  const fallbackBrand = inferBrand(importMeta);
  const fallback = buildUnifiedBaseUrl(fallbackBrand, inferStage(importMeta));
  return sanitizeBaseUrl(fallback);
};
var AuthProvider = ({
  children,
  authApiBaseUrl,
  allowDevBypass = false,
  allowedDomains = [],
  allowedEmails = []
}) => {
  const apiBaseUrl = resolveApiBaseUrl(authApiBaseUrl);
  const normalizedDomains = useMemo(
    () => allowedDomains.map((domain) => domain.trim().toLowerCase()).filter((domain) => domain.length > 0),
    [allowedDomains]
  );
  const normalizedEmails = useMemo(
    () => allowedEmails.map((email) => email.trim().toLowerCase()).filter((email) => email.length > 0),
    [allowedEmails]
  );
  const restrictionsEnabled = normalizedDomains.length > 0 || normalizedEmails.length > 0;
  const isEmailAllowed = (email) => {
    if (!restrictionsEnabled) {
      return true;
    }
    if (!email) return false;
    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail) return false;
    if (normalizedEmails.includes(normalizedEmail)) {
      return true;
    }
    return normalizedDomains.some((domain) => normalizedEmail.endsWith(`@${domain}`));
  };
  const DEV_BYPASS_STORAGE_KEY = "es-dev-bypass-user";
  const readStoredDevBypassUser = useCallback(() => {
    if (!allowDevBypass || typeof window === "undefined") {
      return null;
    }
    try {
      const raw = window.localStorage.getItem(DEV_BYPASS_STORAGE_KEY);
      if (!raw) {
        return null;
      }
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed.email === "string" && typeof parsed.name === "string") {
        return parsed;
      }
    } catch (error) {
      console.warn("Failed to read dev bypass user from storage", error);
    }
    return null;
  }, [allowDevBypass]);
  const writeStoredDevBypassUser = useCallback(
    (next) => {
      if (!allowDevBypass || typeof window === "undefined") {
        return;
      }
      try {
        if (next) {
          window.localStorage.setItem(DEV_BYPASS_STORAGE_KEY, JSON.stringify(next));
        } else {
          window.localStorage.removeItem(DEV_BYPASS_STORAGE_KEY);
        }
      } catch (error) {
        console.warn("Failed to persist dev bypass user", error);
      }
    },
    [allowDevBypass]
  );
  const [user, setUser] = useState(() => readStoredDevBypassUser());
  const [isLoading, setIsLoading] = useState(() => readStoredDevBypassUser() === null);
  const [authError, setAuthError] = useState(null);
  const hasAuthenticatedRef = useRef(readStoredDevBypassUser() !== null);
  const clearAuthError = useCallback(() => {
    setAuthError(null);
  }, []);
  const handleAuthExpiration = useCallback(() => {
    if (!hasAuthenticatedRef.current) {
      setUser(null);
      setIsLoading(false);
      return;
    }
    setAuthError((prev) => prev ?? "Your session expired. Please sign in again.");
    setUser(null);
    setIsLoading(false);
  }, []);
  const fetchSession = useCallback(async () => {
    const storedDevBypassUser = readStoredDevBypassUser();
    if (storedDevBypassUser) {
      setUser(storedDevBypassUser);
      hasAuthenticatedRef.current = true;
      setIsLoading(false);
      return;
    }
    if (!apiBaseUrl) {
      setIsLoading(false);
      return;
    }
    try {
      const response = await fetch(`${apiBaseUrl}/auth/me`, {
        method: "GET",
        credentials: "include"
      });
      if (response.status === 401) {
        handleAuthExpiration();
        return;
      }
      if (!response.ok) {
        setUser(null);
        setIsLoading(false);
        return;
      }
      const payload = await response.json();
      const nextUser = payload.user ?? null;
      setUser(nextUser);
      if (nextUser) {
        hasAuthenticatedRef.current = true;
        setAuthError(null);
      }
    } catch (error) {
      console.warn("Failed to fetch session", error);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, [apiBaseUrl, handleAuthExpiration, readStoredDevBypassUser]);
  useEffect(() => {
    fetchSession();
  }, [fetchSession]);
  useEffect(() => {
    if (typeof window === "undefined" || !apiBaseUrl) {
      return void 0;
    }
    const nativeFetch = window.fetch;
    const monitoredFetch = async (...args) => {
      const response = await nativeFetch(...args);
      try {
        const [requestInfo] = args;
        let requestUrl = "";
        if (typeof requestInfo === "string") {
          requestUrl = requestInfo;
        } else if (typeof URL !== "undefined" && requestInfo instanceof URL) {
          requestUrl = requestInfo.href;
        } else if (typeof Request !== "undefined" && requestInfo instanceof Request) {
          requestUrl = requestInfo.url;
        }
        if (response.status === 401 && requestUrl && requestUrl.startsWith(apiBaseUrl)) {
          if (!readStoredDevBypassUser()) {
            handleAuthExpiration();
          }
        }
      } catch (error) {
        console.warn("Auth interceptor error", error);
      }
      return response;
    };
    window.fetch = monitoredFetch;
    return () => {
      window.fetch = nativeFetch;
    };
  }, [apiBaseUrl, handleAuthExpiration, readStoredDevBypassUser]);
  const login = useCallback(
    async ({ code, devBypass }) => {
      if (allowDevBypass && devBypass) {
        const mockUser = {
          email: "dev@easystreetbrands.com",
          name: "Development User"
        };
        setUser(mockUser);
        writeStoredDevBypassUser(mockUser);
        hasAuthenticatedRef.current = true;
        setAuthError(null);
        return;
      }
      if (!apiBaseUrl) {
        throw new Error("Auth API base URL is not configured");
      }
      if (!code) {
        throw new Error("Missing authorization code");
      }
      const response = await fetch(`${apiBaseUrl}/auth/google`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code })
      });
      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || "Authentication failed");
      }
      const payload = await response.json();
      const authedUser = payload.user;
      if (!isEmailAllowed(authedUser?.email)) {
        setUser(null);
        throw new Error("USER_NOT_AUTHORIZED");
      }
      setUser(authedUser);
      hasAuthenticatedRef.current = true;
      setAuthError(null);
    },
    [allowDevBypass, apiBaseUrl, isEmailAllowed]
  );
  const logout = useCallback(async () => {
    try {
      googleLogout();
    } catch (error) {
      console.warn("Google logout failed", error);
    }
    if (apiBaseUrl) {
      try {
        await fetch(`${apiBaseUrl}/auth/logout`, {
          method: "POST",
          credentials: "include"
        });
      } catch (error) {
        console.warn("Failed to call logout endpoint", error);
      }
    }
    setUser(null);
    writeStoredDevBypassUser(null);
    hasAuthenticatedRef.current = false;
    setAuthError(null);
  }, [apiBaseUrl, writeStoredDevBypassUser]);
  return /* @__PURE__ */ jsx(
    AuthContext.Provider,
    {
      value: {
        user,
        apiBaseUrl,
        login,
        logout,
        isLoading,
        authError,
        clearAuthError
      },
      children
    }
  );
};
var useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

// src/auth/ProtectedRoute.tsx
import { Navigate, useLocation } from "react-router-dom";
import { Fragment, jsx as jsx2, jsxs } from "react/jsx-runtime";
var DefaultLoadingFallback = ({ message }) => /* @__PURE__ */ jsxs("div", { className: "es-auth-loading", children: [
  /* @__PURE__ */ jsx2(
    "style",
    {
      dangerouslySetInnerHTML: {
        __html: `
          .es-auth-loading {
            min-height: 100vh;
            display: flex;
            align-items: center;
            justify-content: center;
            background: #f5f5f5;
            font-family: 'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
            color: #111;
          }

          .es-auth-card {
            background: #fff;
            border: 1px solid #e0e0e0;
            display: flex;
            flex-direction: column;
            align-items: center;
            padding: 2rem 2.5rem;
            text-align: center;
            box-shadow: 0 8px 16px rgba(0, 0, 0, 0.08);
          }

          .es-auth-spinner {
            width: 48px;
            height: 48px;
            border-radius: 50%;
            border: 4px solid #111;
            border-bottom-color: transparent;
            animation: es-auth-spin 0.8s linear infinite;
            margin: 0 auto;
          }

          @keyframes es-auth-spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }

          .es-auth-label {
            margin-top: 1rem;
            font-weight: 500;
            letter-spacing: 0.5px;
          }

          .es-auth-warning {
            display: inline-block;
            margin-top: 0.5rem;
            font-size: 0.85rem;
            color: #b45309;
          }
        `
      }
    }
  ),
  /* @__PURE__ */ jsxs("div", { className: "es-auth-card", children: [
    /* @__PURE__ */ jsx2("div", { className: "es-auth-spinner", "aria-hidden": "true" }),
    /* @__PURE__ */ jsx2("p", { className: "es-auth-label", children: "Checking your session\u2026" }),
    message && /* @__PURE__ */ jsx2("p", { className: "es-auth-warning", children: message })
  ] })
] });
var ProtectedRoute = ({
  children,
  redirectTo = "/login",
  loadingFallback
}) => {
  const { user, isLoading, authError } = useAuth();
  const location = useLocation();
  if (isLoading) {
    if (loadingFallback) {
      return loadingFallback;
    }
    return /* @__PURE__ */ jsx2(DefaultLoadingFallback, { message: authError });
  }
  if (!user) {
    return /* @__PURE__ */ jsx2(Navigate, { to: redirectTo, replace: true, state: { from: location } });
  }
  return /* @__PURE__ */ jsx2(Fragment, { children });
};

// src/auth/LoginView.tsx
import React2, { useEffect as useEffect2, useMemo as useMemo2, useState as useState2 } from "react";
import { useGoogleLogin } from "@react-oauth/google";
import { jsx as jsx3, jsxs as jsxs2 } from "react/jsx-runtime";
var BASE_LOGIN_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;900&family=Open+Sans:wght@300;400;500;600;700&display=swap');

  .es-login-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 100vh;
    background: #f5f5f5;
    padding: 1rem;
  }

  .es-login-card {
    background: #fff;
    padding: 4rem 3rem;
    border-radius: 0;
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.08);
    text-align: center;
    max-width: 480px;
    width: 100%;
    border: 1px solid #e0e0e0;
    position: relative;
    overflow: hidden;
  }

  .es-login-logo {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
    margin-bottom: 3rem;
  }

  .es-logo-es {
    font-family: 'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    font-size: 3rem;
    font-weight: 900;
    color: #000;
    letter-spacing: -2px;
  }

  .es-logo-text {
    font-family: 'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    font-size: 1.25rem;
    font-weight: 300;
    color: #000;
    letter-spacing: 2px;
    text-transform: uppercase;
  }

  .es-login-card h1 {
    font-family: 'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    font-size: 1.6rem;
    font-weight: 700;
    color: #000;
    margin-bottom: 1rem;
    letter-spacing: -0.5px;
  }

  .es-login-subtitle {
    font-family: 'Open Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    color: #666;
    margin-bottom: 3rem;
    font-size: 0.95rem;
    line-height: 1.5;
  }

  .es-login-features {
    display: none;
    margin-bottom: 2rem;
    gap: 1rem;
    justify-content: center;
  }

  .es-feature {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.9rem;
    color: #333;
  }

  .es-feature-icon {
    font-size: 1.15rem;
  }

  .es-google-login-button {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    width: 100%;
    padding: 16px 24px;
    background: #000;
    border: 2px solid #000;
    border-radius: 0;
    font-family: 'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    font-size: 14px;
    font-weight: 600;
    letter-spacing: 1px;
    text-transform: uppercase;
    color: #fff;
    cursor: pointer;
    transition: all 0.3s ease;
    margin-bottom: 1rem;
  }

  .es-google-login-button:hover {
    background: #fff;
    color: #000;
  }

  .es-google-icon {
    width: 20px;
    height: 20px;
  }

  .es-dev-bypass-button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    padding: 14px 24px;
    background: #ffc107;
    border: 2px solid #ffc107;
    border-radius: 0;
    font-family: 'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: #000;
    cursor: pointer;
    transition: all 0.3s ease;
    margin-bottom: 2rem;
  }

  .es-dev-bypass-button:hover {
    background: transparent;
    color: #ffc107;
  }

  .es-login-footer {
    font-size: 0.75rem;
    color: #666;
    margin-bottom: 1.5rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .es-login-error {
    margin: 0 0 1rem 0;
    font-size: 0.85rem;
    font-family: 'Open Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    color: #b91c1c;
  }

  .es-brand-links {
    display: flex;
    justify-content: center;
    gap: 1.5rem;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    flex-wrap: wrap;
  }

  .es-brand-links a {
    color: #000;
    text-decoration: none;
  }

  .es-login-separator {
    color: #999;
  }
`;
var defaultFeatures = [
  { icon: "\u{1F680}", label: "Streamline Operations" },
  { icon: "\u{1F4CA}", label: "Data-Driven Insights" },
  { icon: "\u26A1", label: "Scale Efficiently" }
];
var defaultLinks = [];
var GOOGLE_LOADER_SCOPE = "openid email profile";
var LoginView = ({
  title = "Welcome to the Automation Portal",
  subtitle = "Sign in to access your approved automation workspace.",
  logoPrefix = "AT",
  logoText = "Automation",
  buttonLabel = "Sign in with Google",
  accessDeniedText = "Access denied. Please sign in with an approved Google Workspace account.",
  allowDevBypass = false,
  features = defaultFeatures,
  brandLinks = defaultLinks,
  supportText = "Authorized team members only",
  onLoginSuccess
}) => {
  const { login, authError, clearAuthError } = useAuth();
  const [errorMessage, setErrorMessage] = useState2(null);
  useEffect2(() => {
    if (authError) {
      setErrorMessage(authError);
    }
  }, [authError]);
  const handleAuthError = (error) => {
    console.error("Login failed", error);
    if (error instanceof Error && error.message === "USER_NOT_AUTHORIZED") {
      setErrorMessage(accessDeniedText);
      return;
    }
    setErrorMessage("Login failed. Please try again or contact an administrator.");
  };
  useEffect2(() => {
    return () => {
      clearAuthError();
    };
  }, [clearAuthError]);
  const googleLogin = useGoogleLogin({
    flow: "auth-code",
    ux_mode: "popup",
    redirect_uri: "postmessage",
    scope: GOOGLE_LOADER_SCOPE,
    // `select_account` lets users pick which Google account to use but
    // skips the consent screen for accounts that have already approved
    // the app. Without this override, @react-oauth/google sends
    // `prompt=consent` by default on the auth-code flow and Google
    // re-prompts for permissions on every sign-in.
    prompt: "select_account",
    onSuccess: async (response) => {
      try {
        setErrorMessage(null);
        clearAuthError();
        await login({ code: response.code });
        onLoginSuccess?.();
      } catch (error) {
        handleAuthError(error);
      }
    },
    onError: (error) => {
      handleAuthError(error);
    }
  });
  const handleGoogleLogin = () => {
    if (allowDevBypass) {
      console.log("Google login disabled because dev bypass is active.");
      return;
    }
    googleLogin();
  };
  const handleDevBypass = async () => {
    try {
      setErrorMessage(null);
      clearAuthError();
      await login({ devBypass: true });
      onLoginSuccess?.();
    } catch (error) {
      handleAuthError(error);
    }
  };
  const showFeatures = useMemo2(() => features && features.length > 0, [features]);
  const showBrandLinks = useMemo2(() => brandLinks && brandLinks.length > 0, [brandLinks]);
  return /* @__PURE__ */ jsxs2("div", { className: "es-login-container", children: [
    /* @__PURE__ */ jsx3("style", { dangerouslySetInnerHTML: { __html: BASE_LOGIN_STYLES } }),
    /* @__PURE__ */ jsxs2("div", { className: "es-login-card", children: [
      /* @__PURE__ */ jsxs2("div", { className: "es-login-logo", children: [
        /* @__PURE__ */ jsx3("span", { className: "es-logo-es", children: logoPrefix }),
        /* @__PURE__ */ jsx3("span", { className: "es-logo-text", children: logoText })
      ] }),
      /* @__PURE__ */ jsx3("h1", { children: title }),
      subtitle && /* @__PURE__ */ jsx3("p", { className: "es-login-subtitle", children: subtitle }),
      showFeatures && /* @__PURE__ */ jsx3("div", { className: "es-login-features", children: features.map((feature) => /* @__PURE__ */ jsxs2("div", { className: "es-feature", children: [
        /* @__PURE__ */ jsx3("span", { className: "es-feature-icon", "aria-hidden": "true", children: feature.icon }),
        /* @__PURE__ */ jsx3("span", { children: feature.label })
      ] }, feature.label)) }),
      /* @__PURE__ */ jsxs2("button", { type: "button", onClick: handleGoogleLogin, className: "es-google-login-button", children: [
        /* @__PURE__ */ jsxs2("svg", { className: "es-google-icon", viewBox: "0 0 24 24", "aria-hidden": "true", children: [
          /* @__PURE__ */ jsx3(
            "path",
            {
              fill: "#4285F4",
              d: "M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            }
          ),
          /* @__PURE__ */ jsx3(
            "path",
            {
              fill: "#34A853",
              d: "M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            }
          ),
          /* @__PURE__ */ jsx3(
            "path",
            {
              fill: "#FBBC05",
              d: "M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            }
          ),
          /* @__PURE__ */ jsx3(
            "path",
            {
              fill: "#EA4335",
              d: "M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            }
          )
        ] }),
        buttonLabel
      ] }),
      errorMessage && /* @__PURE__ */ jsx3("p", { className: "es-login-error", children: errorMessage }),
      allowDevBypass && /* @__PURE__ */ jsx3("button", { type: "button", onClick: handleDevBypass, className: "es-dev-bypass-button", children: "Development Login (Bypass)" }),
      supportText && /* @__PURE__ */ jsx3("p", { className: "es-login-footer", children: supportText }),
      showBrandLinks && /* @__PURE__ */ jsx3("div", { className: "es-brand-links", children: brandLinks.map((link, index) => /* @__PURE__ */ jsxs2(React2.Fragment, { children: [
        /* @__PURE__ */ jsx3("a", { href: link.href, target: "_blank", rel: "noopener noreferrer", children: link.label }),
        index < brandLinks.length - 1 && /* @__PURE__ */ jsx3("span", { className: "es-login-separator", children: "\u2022" })
      ] }, link.href)) })
    ] })
  ] });
};

// src/utils/api.ts
var ApiClient = class {
  constructor(baseUrl) {
    this.baseUrl = baseUrl;
    this.headers = {
      "Content-Type": "application/json"
    };
  }
  setAuthToken(token) {
    this.headers["Authorization"] = `Bearer ${token}`;
  }
  clearAuthToken() {
    delete this.headers["Authorization"];
  }
  async request(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;
    const config = {
      ...options,
      headers: {
        ...this.headers,
        ...options.headers || {}
      }
    };
    const response = await fetch(url, config);
    if (!response.ok) {
      throw new Error(`API Error: ${response.statusText}`);
    }
    return response.json();
  }
  get(endpoint) {
    return this.request(endpoint, { method: "GET" });
  }
  post(endpoint, body) {
    return this.request(endpoint, {
      method: "POST",
      body: JSON.stringify(body)
    });
  }
  put(endpoint, body) {
    return this.request(endpoint, {
      method: "PUT",
      body: JSON.stringify(body)
    });
  }
  delete(endpoint) {
    return this.request(endpoint, { method: "DELETE" });
  }
};

// src/utils/auth.ts
var AUTH_TOKEN_KEY = "auth_token";
var USER_KEY = "user";
var authUtils = {
  getToken() {
    return localStorage.getItem(AUTH_TOKEN_KEY) || localStorage.getItem("token");
  },
  setToken(token) {
    localStorage.setItem(AUTH_TOKEN_KEY, token);
    localStorage.setItem("token", token);
  },
  removeToken() {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem("token");
  },
  getUser() {
    const userStr = localStorage.getItem(USER_KEY);
    return userStr ? JSON.parse(userStr) : null;
  },
  setUser(user) {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  },
  removeUser() {
    localStorage.removeItem(USER_KEY);
  },
  isAuthenticated() {
    return !!this.getToken();
  },
  logout() {
    this.removeToken();
    this.removeUser();
    window.location.href = "/login";
  }
};

// src/components/LoadingSpinner.tsx
import { jsx as jsx4, jsxs as jsxs3 } from "react/jsx-runtime";
var LoadingSpinner = ({
  size = "medium",
  message
}) => {
  const sizeClasses = {
    small: "w-8 h-8",
    medium: "w-12 h-12",
    large: "w-16 h-16"
  };
  return /* @__PURE__ */ jsxs3("div", { className: "loading-spinner-container", children: [
    /* @__PURE__ */ jsx4("div", { className: `loading-spinner ${sizeClasses[size]}` }),
    message && /* @__PURE__ */ jsx4("p", { className: "loading-message", children: message })
  ] });
};

// src/components/ErrorBoundary.tsx
import { Component } from "react";
import { jsx as jsx5, jsxs as jsxs4 } from "react/jsx-runtime";
var ErrorBoundary = class extends Component {
  constructor() {
    super(...arguments);
    this.state = {
      hasError: false,
      error: null
    };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, errorInfo) {
    console.error("Uncaught error:", error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }
      return /* @__PURE__ */ jsxs4("div", { className: "error-boundary", children: [
        /* @__PURE__ */ jsx5("h2", { children: "Oops, something went wrong" }),
        /* @__PURE__ */ jsx5("details", { style: { whiteSpace: "pre-wrap" }, children: this.state.error && this.state.error.toString() })
      ] });
    }
    return this.props.children;
  }
};

// src/influencer/client.ts
var buildQueryString = (params = {}) => {
  const query = Object.entries(params).filter(([, value]) => value !== void 0 && value !== null && value !== "").map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`).join("&");
  return query ? `?${query}` : "";
};
var createInfluencerClient = (config) => {
  const fetchImpl = config.fetchFn ?? fetch;
  const authorizedFetch = async (path, init = {}) => {
    const headers = {
      "Content-Type": "application/json",
      ...init.headers
    };
    if (config.apiKey) {
      headers["x-api-key"] = config.apiKey;
    }
    if (config.brandId) {
      headers["x-brand-id"] = config.brandId;
    }
    if (config.environment) {
      headers["x-environment"] = config.environment;
    }
    if (config.getAccessToken) {
      const token = await config.getAccessToken();
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
    }
    const response = await fetchImpl(`${config.baseUrl}${path}`, {
      ...init,
      headers
    });
    if (!response.ok) {
      const body = await response.text();
      throw new Error(`Influencer API request failed (${response.status}): ${body}`);
    }
    if (response.status === 204) {
      return void 0;
    }
    return response.json();
  };
  return {
    creators: {
      list: (filters) => authorizedFetch(`/influencers/creators${buildQueryString(filters ?? {})}`),
      get: (id) => authorizedFetch(`/influencers/creators/${id}`),
      create: (payload) => authorizedFetch(`/influencers/creators`, {
        method: "POST",
        body: JSON.stringify(payload)
      }),
      update: (id, payload) => authorizedFetch(`/influencers/creators/${id}`, {
        method: "PUT",
        body: JSON.stringify(payload)
      })
    },
    campaigns: {
      list: (filters) => authorizedFetch(`/influencers/campaigns${buildQueryString(filters ?? {})}`),
      get: (id) => authorizedFetch(`/influencers/campaigns/${id}`),
      create: (payload) => authorizedFetch(`/influencers/campaigns`, {
        method: "POST",
        body: JSON.stringify(payload)
      }),
      update: (id, payload) => authorizedFetch(`/influencers/campaigns/${id}`, {
        method: "PATCH",
        body: JSON.stringify(payload)
      })
    },
    briefs: {
      list: (filters) => authorizedFetch(`/influencers/briefs${buildQueryString(filters ?? {})}`),
      get: (id) => authorizedFetch(`/influencers/briefs/${id}`),
      create: (payload) => authorizedFetch(`/influencers/briefs`, {
        method: "POST",
        body: JSON.stringify(payload)
      }),
      dispatch: (payload) => authorizedFetch(`/influencers/briefs/${payload.briefId}/dispatch`, {
        method: "POST",
        body: JSON.stringify(payload)
      })
    },
    metrics: {
      listByCreator: (creatorId, params) => authorizedFetch(
        `/influencers/metrics${buildQueryString({ creatorId, ...params })}`
      )
    }
  };
};
export {
  AUTH_TOKEN_KEY,
  ApiClient,
  AuthProvider,
  ErrorBoundary,
  LoadingSpinner,
  LoginView,
  ProtectedRoute,
  USER_KEY,
  authUtils,
  createInfluencerClient,
  resolveApiBaseUrl,
  useAuth
};
